// 2-step Brand signup wizard for InfluenceHub MVP
// Step 1: Company Information (Company Name, Logo optional, Company Bio)
// Step 2: Account Information (First Name, Last Name, Email, Phone, Password, Confirm Password)
// Step 3: Submit Application (simple submission with success screen)
// Matches Figma frames with left logo panel and right form panel

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext.jsx';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../../firebaseConfig.js';
import StepShell from '../../../components/StepShell.jsx';
import ProgressDots from '../../../components/ProgressDots.jsx';
import BrandApplication1 from './steps/BrandApplication1.jsx';
import BrandApplication2 from './steps/BrandApplication2.jsx';
import BrandSubmission from './steps/BrandSubmission.jsx';

const BrandWizard = ({ onNavigate, queryParams = '' }) => {
  const { user, profile, loading } = useAuth();
  
  // Wizard state management
  const [currentStep, setCurrentStep] = useState(0);
  const [wizardState, setWizardState] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Define step configurations (2-step wizard)
  const steps = [
    { 
      component: BrandApplication1, 
      title: 'Company Information',
      subtitle: 'Tell us about your company!'
    },
    { 
      component: BrandApplication2, 
      title: 'Account Information',
      subtitle: 'Create your account'
    },
    { 
      component: BrandSubmission, 
      title: 'Submit Application',
      subtitle: 'Complete your brand registration'
    }
  ];

  const totalSteps = steps.length;
  const currentStepConfig = steps[currentStep];

  // Handle fresh parameter - clear localStorage data
  useEffect(() => {
    const urlParams = new URLSearchParams(queryParams);
    const isFresh = urlParams.get('fresh') === '1';
    
    if (isFresh) {
      // Clear all wizard draft data
      localStorage.removeItem('ih.wizardDraft.preAuth');
      
      // Clear any existing uid-based drafts
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('ih.wizardDraft.')) {
          localStorage.removeItem(key);
        }
      });
      
      // Ensure we start at Step 0 with empty state
      setCurrentStep(0);
      setWizardState({});
    }
  }, [queryParams]);

  // Clear any cached data that might have old contactName validation
  useEffect(() => {
    // Clear any cached data that might contain old contactName field
    const preAuthData = localStorage.getItem('ih.wizardDraft.preAuth');
    if (preAuthData) {
      try {
        const parsedData = JSON.parse(preAuthData);
        // If the cached data has contactName, clear it
        if (parsedData.account?.contactName) {
          console.log('Clearing cached data with old contactName field');
          localStorage.removeItem('ih.wizardDraft.preAuth');
          setWizardState({});
        }
      } catch (error) {
        console.error('Error checking cached data:', error);
      }
    }
    
    // Also clear any other cached data that might have old validation
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('ih.wizardDraft.')) {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          if (data.account?.contactName) {
            console.log('Clearing cached data with old contactName field:', key);
            localStorage.removeItem(key);
          }
        } catch (error) {
          // Ignore parsing errors
        }
      }
    });
  }, []);

  // Redirect logic based on authentication and onboarding status
  useEffect(() => {
    if (!loading) {
      const urlParams = new URLSearchParams(queryParams);
      const isFresh = urlParams.get('fresh') === '1';
      
      // If user is authenticated and onboarding is complete, redirect to dashboard (unless fresh)
      if (user && profile && profile.onboardingComplete && !isFresh) {
        onNavigate('dashboard');
        return;
      }
      
      // If user is authenticated but not a brand, redirect to dashboard (unless fresh)
      if (user && profile && profile.userType !== 'brand' && !isFresh) {
        onNavigate('dashboard');
        return;
      }
      
      // If user is authenticated and is a brand, load draft data (only if not fresh)
      if (user && profile && profile.userType === 'brand' && !isFresh) {
        const savedData = localStorage.getItem(`ih.wizardDraft.${user.uid}`);
        if (savedData) {
          try {
            const parsedData = JSON.parse(savedData);
            setWizardState(parsedData);
            // Restore saved step if available, otherwise start at Step 0
            if (parsedData.currentStep !== undefined) {
              setCurrentStep(parsedData.currentStep);
            }
          } catch (error) {
            console.error('Error loading wizard data:', error);
          }
        }
      }
      
      // If no user is authenticated, allow them to proceed with brand signup
      // This is the key fix - don't redirect unauthenticated users
    }
  }, [user, profile, loading, onNavigate, queryParams]);

  // Load preAuth draft data if user is not authenticated (Step 1)
  useEffect(() => {
    if (!user) {
      const urlParams = new URLSearchParams(queryParams);
      const isFresh = urlParams.get('fresh') === '1';
      
      // Only load preAuth data if not in fresh mode
      if (!isFresh) {
        const preAuthData = localStorage.getItem('ih.wizardDraft.preAuth');
        if (preAuthData) {
          try {
            const parsedData = JSON.parse(preAuthData);
            setWizardState(parsedData);
            if (parsedData.currentStep !== undefined) {
              setCurrentStep(parsedData.currentStep);
            }
          } catch (error) {
            console.error('Error loading preAuth data:', error);
          }
        }
      }
    }
  }, [user, queryParams]);

  // Save wizard data to localStorage on every change
  useEffect(() => {
    if (user?.uid && Object.keys(wizardState).length > 0) {
      const dataToSave = { ...wizardState, currentStep };
      localStorage.setItem(`ih.wizardDraft.${user.uid}`, JSON.stringify(dataToSave));
    } else if (!user && Object.keys(wizardState).length > 0) {
      // Save to preAuth draft if user not authenticated yet
      const dataToSave = { ...wizardState, currentStep };
      localStorage.setItem('ih.wizardDraft.preAuth', JSON.stringify(dataToSave));
    }
  }, [wizardState, currentStep, user?.uid]);

  // Handle step data updates
  const handleStepDataUpdate = (stepData) => {
    const newState = {
      ...wizardState,
      ...stepData
    };
    setWizardState(newState);
    
    // Save to localStorage if user is authenticated
    if (user?.uid) {
      const dataToSave = {
        ...newState,
        currentStep
      };
      localStorage.setItem(`ih.wizardDraft.${user.uid}`, JSON.stringify(dataToSave));
    } else {
      // Save to preAuth draft if not authenticated
      const dataToSave = {
        ...newState,
        currentStep
      };
      localStorage.setItem('ih.wizardDraft.preAuth', JSON.stringify(dataToSave));
    }
  };

  // Navigate to next step
  const handleNext = async () => {
    console.log('🔍 BrandWizard handleNext called, validating step:', currentStep);
    console.log('🔍 Current step component:', currentStepConfig.component.name);
    console.log('🔍 Wizard state:', wizardState);
    
    // Validate current step
    const validation = await currentStepConfig.component.validate?.(wizardState);
    console.log('🔍 Validation result:', validation);
    
    if (validation && !validation.valid) {
      console.log('❌ Validation failed with errors:', validation.errors);
      setError(validation.errors?.join(', ') || 'Please fix the errors above');
      return;
    }

    // Move to next step
    if (currentStep < totalSteps - 1) {
      const newStep = currentStep + 1;
      setCurrentStep(newStep);
      setError('');
      
      // Save the new step to localStorage
      if (user?.uid) {
        const dataToSave = {
          ...wizardState,
          currentStep: newStep
        };
        localStorage.setItem(`ih.wizardDraft.${user.uid}`, JSON.stringify(dataToSave));
      } else {
        const dataToSave = {
          ...wizardState,
          currentStep: newStep
        };
        localStorage.setItem('ih.wizardDraft.preAuth', JSON.stringify(dataToSave));
      }
    }
  };

  // Navigate to previous step
  const handleBack = () => {
    if (currentStep > 0) {
      const newStep = currentStep - 1;
      setCurrentStep(newStep);
      setError('');
      
      // Save the new step to localStorage
      if (user?.uid) {
        const dataToSave = {
          ...wizardState,
          currentStep: newStep
        };
        localStorage.setItem(`ih.wizardDraft.${user.uid}`, JSON.stringify(dataToSave));
      } else {
        const dataToSave = {
          ...wizardState,
          currentStep: newStep
        };
        localStorage.setItem('ih.wizardDraft.preAuth', JSON.stringify(dataToSave));
      }
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show error if user is authenticated but not a brand
  if (user && profile && profile.userType !== 'brand') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600">This wizard is for brands only.</p>
          <button 
            onClick={() => onNavigate('dashboard')}
            className="mt-4 btn-primary"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Render current step
  const CurrentStepComponent = currentStepConfig.component;
  const isFirstStep = currentStep === 0;

  // Create footer buttons based on step
  // Steps 0 and 1 (BrandApplication1 and BrandApplication2) have their own inline buttons
  // Final step (Submit/BrandSubmission) handles its own buttons
  // So we don't need footer buttons for any step
  const isFinalStep = currentStep === totalSteps - 1;
  
  // No footer buttons - all steps handle their own buttons inline
  const footerLeft = null;
  const footerRight = null;

  // Progress dots: show 2 steps for the main wizard steps (0 and 1)
  // Step 2 (index 2) is the submission step, show as complete (2/2)
  const progressCurrent = currentStep < 2 ? currentStep : 1;
  const progressTotal = 2;

  return (
    <StepShell
      title={currentStepConfig.title}
      subtitle={currentStepConfig.subtitle}
      progressDots={{ current: progressCurrent, total: progressTotal }}
      footerLeft={footerLeft}
      footerRight={footerRight}
    >
      {/* Error display */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
          {error}
        </div>
      )}

      {/* Step content */}
      <CurrentStepComponent
        data={wizardState}
        onDataChange={handleStepDataUpdate}
        onNext={handleNext}
        onBack={handleBack}
        onNavigate={onNavigate}
        user={user}
      />
    </StepShell>
  );
};

export default BrandWizard;
